























import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import http from "@/axios/http-common";

export interface UnitRuangOption {
  id: string;
  nama: string;
  kategori: string | null; // ralan | penunjang | ranap | null
}

const KATEGORI_LABEL: Record<string, string> = {
  ranap: "Rawat Inap",
  ralan: "Rawat Jalan / IGD",
  penunjang: "Penunjang",
  lainnya: "Lainnya"
};

const KATEGORI_ORDER = ["ranap", "ralan", "penunjang", "lainnya"];

let unitCache: Promise<UnitRuangOption[]> | null = null;

export function fetchUnits(): Promise<UnitRuangOption[]> {
  if (!unitCache) {
    unitCache = http
      .getData("/referensi/unit-pelayanan")
      .then(res => {
        const body = res.data;
        const list = body?.data || body || [];
        return (Array.isArray(list) ? list : []).map((u: any) => ({
          id: String(u.id ?? u.KODE ?? u.kode ?? ""),
          nama: u.nama ?? u.UNIT ?? u.text ?? "",
          kategori: u.kategori ?? null
        }));
      })
      .catch(err => {
        unitCache = null;
        throw err;
      });
  }
  return unitCache;
}

export function namaUnitPelayanan(unitId: string): Promise<string> {
  const kode = (unitId || "").trim();
  if (!kode) return Promise.resolve("");
  return fetchUnits()
    .then(units => {
      const unit = units.find(u => u.id.trim() === kode);
      return unit ? unit.nama : "";
    })
    .catch(() => "");
}

@Component
export default class UnitRuangPicker extends Vue {
  @Prop({ default: null }) readonly value!: string | string[] | null;
  @Prop({ default: "Unit / Ruangan" }) readonly label!: string;
  @Prop({ default: () => [] }) readonly kategori!: string[];
  @Prop({ type: Boolean, default: false }) readonly includeIgd!: boolean;
  @Prop({ default: () => [] }) readonly includeUnits!: string[];
  @Prop({ default: () => [] }) readonly excludeUnits!: string[];
  @Prop({ default: "id" }) readonly itemValue!: string;
  @Prop({ type: Boolean, default: false }) readonly multiple!: boolean;
  @Prop({ type: Boolean, default: false }) readonly clearable!: boolean;
  @Prop({ type: Boolean, default: false }) readonly clearInvalid!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ default: () => [] }) readonly errorMessages!: string[];
  @Prop({ type: [Boolean, String], default: false }) readonly hideDetails!:
    | boolean
    | string;
  @Prop({ default: () => [] }) readonly rules!: unknown[];

  units: UnitRuangOption[] = [];
  loading = false;

  async mounted() {
    this.loading = true;
    try {
      this.units = await fetchUnits();
      if (this.clearInvalid) this.onKategoriChange();
    } catch (e) {
      this.units = [];
    } finally {
      this.loading = false;
    }
  }

  get filteredUnits(): UnitRuangOption[] {
    const units = this.excludeUnits.length
      ? this.units.filter(u => this.excludeUnits.indexOf(u.id) === -1)
      : this.units;

    if (!this.kategori.length) return units;
    return units.filter(u => {
      if (this.includeIgd && u.id === "01") return true;
      if (this.includeUnits.indexOf(u.id) > -1) return true;
      return u.kategori !== null && this.kategori.indexOf(u.kategori) > -1;
    });
  }

  get displayItems(): unknown[] {
    const grouped: Record<string, UnitRuangOption[]> = {};
    this.filteredUnits.forEach(u => {
      const key = u.kategori || "lainnya";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(u);
    });

    const keys = KATEGORI_ORDER.filter(k => grouped[k] && grouped[k].length);
    if (keys.length <= 1) return this.filteredUnits;

    const items: unknown[] = [];
    keys.forEach(k => {
      items.push({ header: KATEGORI_LABEL[k] });
      grouped[k].forEach(u => items.push(u));
    });
    return items;
  }

  onInput(val: string | string[] | null) {
    if (this.multiple) {
      const arr = Array.isArray(val) ? val : [];
      this.$emit("input", arr);
      this.$emit(
        "change",
        this.filteredUnits.filter(u => arr.indexOf(u[this.itemValue]) > -1)
      );
      return;
    }
    const tunggal = Array.isArray(val) ? val[0] || null : val;
    this.$emit("input", tunggal);
    const unit = tunggal
      ? this.filteredUnits.find(u => u[this.itemValue] === tunggal) || null
      : null;
    this.$emit("change", unit);
  }

  @Watch("kategori")
  @Watch("includeUnits")
  @Watch("excludeUnits")
  onKategoriChange() {
    // Bila filter berubah dan unit terpilih tak lagi valid, kosongkan.
    if (!this.units.length) return;
    if (this.multiple) {
      const saatIni = Array.isArray(this.value) ? this.value : [];
      const valid = saatIni.filter(v =>
        this.filteredUnits.some(u => u[this.itemValue] === v)
      );
      if (valid.length !== saatIni.length) {
        this.onInput(valid);
      }
      return;
    }
    if (
      this.value &&
      !this.filteredUnits.some(u => u[this.itemValue] === this.value)
    ) {
      this.onInput(null);
    }
  }
}
